import {
  AimOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  SaveOutlined,
  ScanOutlined,
} from '@ant-design/icons'
import { Button } from 'antd'
import { t } from 'i18next'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { styled } from 'styled-components'
import {
  BackButton,
  Badge,
  ConfirmDeleteModal,
  Spacer,
} from '../../../components'
import { BadgeColors } from '../../../constants/constants'
import {
  useMediaQuery,
  useOfrDocumentTemplate,
  useOfrDocumentTemplateDetails,
} from '../../../hooks'
import { OfrDocumentTemplate } from '../../../store/reducers/ofrDocumentTemplateReducer'
import { routePaths } from '../../RootPage'
const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  gap: 16px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;
`

const PaddedColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 0;
`
const TitleContainer = styled.div`
  display: flex;
  margin-top: auto;
  margin-bottom: auto;
  gap: 8px;
  color: ${(props) => props.theme.token.colorText};
`

interface OfrDocumentTemplateDetailsPageHeaderProps {
  onSave: () => void
  ofrDocumentTemplate?: OfrDocumentTemplate
}

const OfrDocumentTemplateDetailsPageHeader = ({
  onSave,
  ofrDocumentTemplate,
}: OfrDocumentTemplateDetailsPageHeaderProps) => {
  const navigate = useNavigate()
  const { formId } = useParams()
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { deleteOfrDocumentTemplate } = useOfrDocumentTemplate({})
  const { isDeletable, isUpdatable } = useOfrDocumentTemplateDetails({})
  const isMobile = useMediaQuery('xs')

  const handleCancel = () => {
    setConfirmDeleteModalOpen(false)
  }

  const handleConfirmDelete = () => {
    setDeleteLoading(true)
    setConfirmDeleteModalOpen(false)
    setDeleteLoading(true)
    if (formId) {
      deleteOfrDocumentTemplate({
        documentId: formId,
        navigate: null,
      })
    }
    setConfirmDeleteModalOpen(false)

    setDeleteLoading(false)
    navigate(routePaths.OFR_DOCUMENT_TEMPLATE_LIST)
  }

  const onOpenDeleteModal = () => {
    setConfirmDeleteModalOpen(true)
  }

  const dataSourceTitle = useMemo(() => {
    return ofrDocumentTemplate?.dataSourceTitle
  }, [ofrDocumentTemplate])

  const documentName = useMemo(() => {
    return ofrDocumentTemplate?.documentName
  }, [ofrDocumentTemplate])

  return (
    <HeaderContainer>
      <PaddedColumn>
        <Row>
          <BackButton
            url={routePaths.OFR_DOCUMENT_TEMPLATE_LIST}
            tooltipMessage={t(
              'ofrDocumentTemplateDetailsPage.backButtonTooltip'
            )}
          />
          <TitleContainer>
            <AimOutlined />
            <div>{t('Configuration')}</div>
          </TitleContainer>
          <Spacer />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={onOpenDeleteModal}
            loading={deleteLoading}
            disabled={!isDeletable}
          >
            {!isMobile &&
              t('ofrDocumentTemplateDetailsPage.deleteDocumentTemplateButton')}
          </Button>
          <ConfirmDeleteModal
            open={confirmDeleteModalOpen}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancel}
            loading={deleteLoading}
            confirmButtonLabel={
              t('ofrDocumentTemplateListPage.confirmDeleteModal.yesButton') ||
              ''
            }
          >
            {isMobile &&
              t('ofrDocumentTemplateListPage.confirmDeleteModal.content')}
          </ConfirmDeleteModal>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            htmlType="submit"
            loading={false}
            onClick={onSave}
            disabled={!isUpdatable}
          >
            {!isMobile && t('saveButton')}
          </Button>
        </Row>

        <Row>
          <Badge icon={<DatabaseOutlined />} color={BadgeColors.DATA_SOURCE}>
            {dataSourceTitle}
          </Badge>
          <Badge icon={<ScanOutlined />} color={BadgeColors.VOLCANO}>
            {documentName}
          </Badge>
        </Row>
      </PaddedColumn>
    </HeaderContainer>
  )
}

export default OfrDocumentTemplateDetailsPageHeader
